import React, { useEffect, useState } from "react";
import { Mail, Moon, Swords } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";
import api from "../../api";

const OPTIONS = ["instant", "daily", "weekly", "off"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

// "tomorrow after 08:00" reads better than a raw timestamp.
const nextLabel = (iso, t) => {
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now.getTime() + 86400000).toDateString() === d.toDateString();
  if (d.getTime() - now.getTime() < 120000) return t("acc.nextEmailSoon");
  if (sameDay) return t("acc.nextEmailToday", { time });
  if (tomorrow) return t("acc.nextEmailTomorrow", { time });
  return `${d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })} · ${time}`;
};

export const EmailFrequencyCard = () => {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const { toast } = useToast();
  const [next, setNext] = useState(null);
  const cadence = user?.emailPrefs?.cadence || "daily";
  const quiet = user?.emailPrefs?.quietHours || { enabled: true, start: 21, end: 8 };
  const rivalAlerts = user?.emailPrefs?.rivalAlerts !== false;

  const saveRivals = async (on) => {
    try {
      await api.put("/my/email-prefs", { rivalAlerts: on });
      await refresh();
      toast({ title: on ? t("acc.rivalAlertsOn") : t("acc.rivalAlertsOff") });
    } catch { toast({ title: t("acc.couldNotUpdatePreferences") }); }
  };

  const loadNext = () => api.get("/my/next-email").then((r) => setNext(r.data)).catch(() => setNext(null));
  useEffect(() => { loadNext(); }, [user?.emailPrefs?.cadence, user?.emailPrefs?.quietHours?.start,
    user?.emailPrefs?.quietHours?.end, user?.emailPrefs?.quietHours?.enabled]);

  const save = async (value) => {
    try {
      await api.put("/my/email-prefs", { cadence: value });
      await refresh();
      toast({ title: t("acc.emailFreqSaved"), description: t(`acc.freq_${value}_note`) });
    } catch { toast({ title: t("acc.couldNotUpdatePreferences") }); }
  };

  const saveQuiet = async (patch) => {
    const next = {
      enabled: patch.enabled ?? quiet.enabled,
      start: patch.start ?? quiet.start,
      end: patch.end ?? quiet.end,
      tzOffset: -new Date().getTimezoneOffset(),
    };
    try {
      await api.put("/my/email-prefs", { quietHours: next });
      await refresh();
      toast({ title: t("acc.quietSaved") });
    } catch { toast({ title: t("acc.couldNotUpdatePreferences") }); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="email-prefs-card">
      <h3 className="font-bold text-foreground flex items-center gap-2 mb-1"><Mail size={18} className="text-[#16a34a]" /> {t("acc.emailPreferences")}</h3>
      <p className="text-xs text-muted-foreground mb-3">{t("acc.emailFreqIntro")}</p>
      <div className="space-y-2">
        {OPTIONS.map((v) => (
          <button key={v} type="button" onClick={() => save(v)} data-testid={`email-cadence-${v}`}
            className={`w-full flex items-center justify-between gap-3 border rounded-xl px-3 py-3 text-left transition-colors ${cadence === v
              ? "border-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f]"
              : "border-border hover:border-[#16a34a]/50"}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t(`acc.freq_${v}`)}</p>
              <p className="text-xs text-muted-foreground">{t(`acc.freq_${v}_note`)}</p>
            </div>
            <span className={`h-5 w-5 rounded-full border-2 shrink-0 grid place-items-center ${cadence === v ? "border-[#16a34a]" : "border-border"}`}>
              {cadence === v && <span className="h-2.5 w-2.5 rounded-full bg-[#16a34a]" />}
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3" data-testid="email-cap-note">{t("acc.emailCapNote")}</p>

      {user?.role === "business" && (
        <div className="mt-4 pt-4 border-t border-border" data-testid="rival-alerts">
          <button type="button" onClick={() => saveRivals(!rivalAlerts)} data-testid="rival-alerts-toggle"
            className="w-full flex items-center justify-between gap-3 text-left">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Swords size={14} className="text-[#16a34a]" /> {t("acc.rivalAlertsTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("acc.rivalAlertsNote")}</p>
            </div>
            <span className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${rivalAlerts ? "bg-[#16a34a]" : "bg-muted"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${rivalAlerts ? "left-[22px]" : "left-0.5"}`} />
            </span>
          </button>
        </div>
      )}

      {cadence !== "off" && (
        <div className="mt-4 pt-4 border-t border-border" data-testid="quiet-hours">
          <button type="button" onClick={() => saveQuiet({ enabled: !quiet.enabled })}
            data-testid="quiet-hours-toggle"
            className="w-full flex items-center justify-between gap-3 text-left">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Moon size={14} className="text-[#16a34a]" /> {t("acc.quietTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("acc.quietNote")}</p>
            </div>
            <span className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${quiet.enabled ? "bg-[#16a34a]" : "bg-muted"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${quiet.enabled ? "left-[22px]" : "left-0.5"}`} />
            </span>
          </button>
          {quiet.enabled && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">{t("acc.quietFrom")}</span>
              <select value={quiet.start} onChange={(e) => saveQuiet({ start: Number(e.target.value) })}
                data-testid="quiet-start"
                className="border border-border rounded-xl px-2.5 py-2 bg-transparent font-semibold text-foreground outline-none focus:border-[#16a34a]">
                {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
              <span className="text-muted-foreground">{t("acc.quietTo")}</span>
              <select value={quiet.end} onChange={(e) => saveQuiet({ end: Number(e.target.value) })}
                data-testid="quiet-end"
                className="border border-border rounded-xl px-2.5 py-2 bg-transparent font-semibold text-foreground outline-none focus:border-[#16a34a]">
                {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
              <span className="text-muted-foreground">{t("acc.quietYourTime")}</span>
            </div>
          )}
          {next && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="next-email-preview">
              {next.state === "off"
                ? t("acc.nextEmailOff")
                : t("acc.nextEmailAt", { when: nextLabel(next.nextAt, t) })}
              {next.pending > 0 && next.state !== "off" && ` · ${t("acc.nextEmailWaiting", { n: next.pending })}`}
            </p>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">{t("acc.accountSecurityEmailsReceipts")}</p>
    </div>
  );
};
